import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { ColumnVisibility } from '../../../core/model/column-definition';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { CommandFactoryService } from '../../../core/service/factory/command-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { rentalObjectCommand } from '../../command/rental-object.command';
import { Parking } from '../../model/parking';
import { ParkingRentalObjectCommandFactory } from '../../service/command/factory/parking-rental-object-command-factory.service';
import { ParkingCommandHandlerResolver } from '../../service/command/parking-command-handler-resolver.service';
import { CompanyService } from '../../service/data/company.service';
import { ParkingService } from '../../service/data/parking.service';
import { PropertyService } from '../../service/data/property.service';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { AllowSort } from '../../../core/service/factory/allow-sort';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { CustomPropertyVisibilityType, FormOfLease, FormOfLeaseToString } from '../../model/const/enums';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { LettingFeatureFilterHelper } from '../../helper/letting-feature-filter-helper';
import { LettingFeatureColumnHelper } from '../../helper/letting-feature-column-helper';
import { LettingFeatureService } from '../../service/data/letting-feature.service';
import { standardCommand } from '../../../core/model/command';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-parking-rental-object-manager',
    templateUrl: 'parking-rental-object-manager.component.html',
    styleUrls: [
        'parking-rental-object-manager.component.scss',
        './../../../core/component/multiselect-object-manager/multiselect-object-manager.component.scss',
    ],
    providers: [ParkingCommandHandlerResolver, { provide: CommandFactoryService, useClass: ParkingRentalObjectCommandFactory }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ParkingRentalObjectManagerComponent extends PagedObjectManagerComponent<Parking> {
  constructor(
    private readonly cdf: ColumnDefinitionFactory,
    private _companyService: CompanyService,
    private _propertyService: PropertyService,
    private _dataParkingService: ParkingService,
    private _accountService: UserAccountService,
    dispatcher: CommandDispatcher,
    messageService: MessageService,
    navigationService: NavigationService,
    handlerResolver: ParkingCommandHandlerResolver,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _ch: CustomPropertyHelper,
    private readonly _lettingFeatureService: LettingFeatureService,
    private readonly _lettingFeatureFilterHelper: LettingFeatureFilterHelper,
    private readonly _lettingFeatureColumnHelper: LettingFeatureColumnHelper
  ) {
    super(_dataParkingService, messageService, navigationService, dispatcher, handlerResolver);
    this.columnDefinition = [
      cdf.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      cdf.create('RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
      cdf.create('RENTAL_OBJECT_LIST.PARKING_NUMBER_COLUMN', 'parkingNumber'),
      cdf.create('RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
      cdf.create('RENTAL_OBJECT_FORM.SURFACE_LABEL', 'surface', ColumnVisibility.optional),
      cdf.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      cdf.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      cdf.create('PROPERTY.NUMBER', 'propertyNumber', ColumnVisibility.optional),
      cdf.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      cdf.create('LEASE_AGREEMENT.CONTRACT_HOLDER_ONE', 'tenantOneName'),
      cdf.createPhoneNumber('LEASE_AGREEMENT.CONTRACT_HOLDER_ONE_PHONE', 'tenantOnePhone', ColumnVisibility.optional),
      cdf.create('LEASE_AGREEMENT.CONTRACT_HOLDER_ONE_EMAIL', 'tenantOneEmail', ColumnVisibility.optional),
      cdf.createDate('COMMON.AGREEMENT_START_DATE', 'leaseAgreementFromDate', ColumnVisibility.optional),
      cdf.createDate('COMMON.AGREEMENT_END_DATE', 'leaseAgreementLastBillingDate', ColumnVisibility.optional),
      cdf.createDate('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom', ColumnVisibility.optional),
      cdf.createDate('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo', ColumnVisibility.optional),
      cdf.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
      cdf.createTranslation('RENTAL_OBJECT.FORM_OF_LEASE', 'formOfLeaseText', ColumnVisibility.optional),
      cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent', {
        allowSort: this,
      }),
      cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN_LEASE_AGREEMENT', 'currentMonthRentLeaseAgreement', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('RENTAL_OBJECT_LIST.RENT_LOSS_MONTH_CURRENT_COLUMN', 'rentLossCurrentMonth', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_NEXT_COLUMN', 'monthlyRentNext', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_NEXT_COLUMN_LEASE_AGREEMENT', 'nextMonthRentLeaseAgreement', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('RENTAL_OBJECT_LIST.RENT_LOSS_MONTH_NEXT_COLUMN', 'rentLossNextMonth', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('COMMON.YEAR_RENT', 'yearRent', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('COMMON.RENT_LOSS_THIS_YEAR', 'rentLossCurrentYear', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('COMMON.RENT_LOSS_PREVIOUS_YEAR', 'rentLossPreviousYear', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
    ];

    this.menuDefinition = [new MenuItemDefinition('COMMON.EXCEL_EXPORT', rentalObjectCommand.export)];
    this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.RENTAL_CHANGE', rentalObjectCommand.feeChange);

    this.preferencesKey = 'rent.parking-rental-object-manager.list';

    this.AddSubscription(
      this._accountService.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Properties, Features.EditParking)) {
            this.allowNew = true;
          } else {
            this.allowNew = false;
          }
          if (account.hasAccess(ModuleNames.Properties, Features.ChangeRentalObjectCategory)) {
            this.addContextMenuItemDefinition('RENTAL_OBJECT_CATEGORY.COMMAND', rentalObjectCommand.changeCategory);
          }

          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', rentalObjectCommand.showInvoices);
          if (
            account.hasAccess(ModuleNames.Properties, Features.LinkPropertyBaseDocuments) ||
            account.hasAccess(ModuleNames.Properties, Features.EditParking)
          ) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', rentalObjectCommand.linkDocument);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_DOCUMENTS', rentalObjectCommand.showDocuments);

          if (account.hasAccess(ModuleNames.Letting, Features.Letting_DhMarket)) {
            this.addContextMenuItemDefinition('RENTAL_OBJECT.COMMAND.ADVERTISE', rentalObjectCommand.advertise);
          }

          this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
          if (this.allowNew) {
            this.addContextMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          }
        },
      })
    );

    this.title = 'RENTAL_OBJECT_LIST.PARKING_HEADER';
  }
  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
    this._lettingFeatureFilterHelper.applyLettingFeatureFilters(filterState);
  }

  ngOnInit() {
    this.AddSubscription(
      forkJoin([this._customPropertyService.getList(), this._dataParkingService.getFilterDefinition(), this._lettingFeatureService.getAvailableForRentalObjects()]).subscribe(
        ([customProperties, definition, lettingFeatures]) => {
          this.defaultFilters = definition.defaultFields;
          const rentalObjectTypes = definition.rentalObjectTypes.map(x => {
            return { caption: x.name, value: x.id };
          });
          rentalObjectTypes.splice(0, 0, { caption: 'ALL_TEXT', value: '' });
          const articles = definition.articles.map(article => {
            return { caption: article.toString(), value: article.id };
          });
          articles.splice(0, 0, { caption: 'ALL_TEXT', value: '' });

          var formOfLeaseSelection = [
            { caption: 'ALL_TEXT', value: null },
            { caption: FormOfLeaseToString(FormOfLease.Lease), value: FormOfLease.Lease },
            { caption: FormOfLeaseToString(FormOfLease.CooperativeHousing), value: FormOfLease.CooperativeHousing },
            { caption: FormOfLeaseToString(FormOfLease.Ownership), value: FormOfLease.Ownership },
          ];

          const selectedCompanies = definition.companyIds
            ? Array.isArray(definition.companyIds)
              ? definition.companyIds
              : [definition.companyIds]
            : [];

          const selectedProperties = definition.propertyIds
            ? Array.isArray(definition.propertyIds)
              ? definition.propertyIds
              : [definition.propertyIds]
            : [];

          this.filterDefinition = [
            new FilterDefinitionItem(
              'COMPANY.LIST_HEADER',
              'companyIds',
              selectedCompanies,
              this._companyService,
              definition.selectedFields.includes('companyIds') || definition.selectedFields.includes('company'),
              true
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
              'propertyIds',
              selectedProperties,
              this._propertyService,
              definition.selectedFields.includes('propertyIds') || definition.selectedFields.includes('property'),
              true
            ),
            new FilterDefinitionItem(
              'COMMON.RENTAL_OBJECT_TYPE',
              'rentalObjectType',
              definition.rentalObjectType,
              rentalObjectTypes,
              definition.selectedFields.includes('rentalObjectType')
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL',
              'rentalId',
              definition.rentalId,
              null,
              definition.selectedFields.includes('rentalId')
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT_LIST.STREET_COLUMN',
              'street',
              definition.street,
              null,
              definition.selectedFields.includes('street')
            ),
            new FilterDefinitionItem(
              'ARTICLE.EDIT_HEADER',
              'article',
              definition.article,
              articles,
              definition.selectedFields.includes('article')
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT.FORM_OF_LEASE',
              'formOfLease',
              definition.formOfLease,
              formOfLeaseSelection,
              definition.selectedFields.includes('formOfLease')
            ),
          ];

          const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
            customProperties,
            definition,
            CustomPropertyVisibilityType.Company | CustomPropertyVisibilityType.Property
          );
          this._ch.createCustomPropertyColumnDefinition(
            customProperties,
            this.columnDefinition,
            CustomPropertyVisibilityType.Company | CustomPropertyVisibilityType.Property
          );
          this._lettingFeatureColumnHelper.createLettingFeatureColumnDefinitions(lettingFeatures, this.columnDefinition);
          const lettingFeatureFilters = this._lettingFeatureFilterHelper.generateLettingFeatureFilters(lettingFeatures, definition);
          this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters, ...lettingFeatureFilters];
        }
      )
    );
  }
}
